import { useEffect, useRef } from "react";
import {
  useForm as useFormHook,
  UseFormProps as UseFormPropsHook,
  DefaultValues,
} from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { TypeOf, ZodSchema } from "zod";
import { z } from "zod";

interface UseFormProps<Z extends ZodSchema>
  extends Omit<UseFormPropsHook<TypeOf<Z>>, "resolver"> {
  defaultValues?: DefaultValues<TypeOf<Z>>;
  schema: Z;
  onSubmit: (data: z.infer<Z>) => void;
  onClose?: () => void;
}

export const useForm = <Z extends ZodSchema>({
  defaultValues,
  mode = "all",
  schema,
  onSubmit,
  onClose,
  ...formProps
}: UseFormProps<Z>) => {
  const hasUserInteracted = useRef(false);

  const form = useFormHook({
    ...formProps,
    resolver: zodResolver(schema),
    mode,
    defaultValues: defaultValues as any,
  });

  const { reset, setError, getValues } = form;

  const handleFormSubmit = async (data: z.infer<typeof schema>) => {
    try {
      await onSubmit(data);

      // Reset after submit to sync state
      reset(getValues());
    } catch (error) {
      setError("root", {
        type: "manual",
        message:
          error instanceof Error ? error.message : "Failed to submit form",
      });
    }
  };

  // Track when user starts interacting with the form
  useEffect(() => {
    const subscription = form.watch(() => {
      hasUserInteracted.current = true;
    });
    return () => subscription.unsubscribe();
  }, [form]);

  useEffect(() => {
    // Only reset the form if the user hasn't interacted with it
    if (!hasUserInteracted.current) {
      form.reset(defaultValues);
    }
  }, [defaultValues]);

  const handleClose = () => {
    reset();
    onClose?.();
  };

  const handleSubmit = form.handleSubmit(handleFormSubmit);

  return { form, handleSubmit, handleClose };
};

export default useForm;
